<template>
  <div class="line-chart">
    <div>
      <div  class="mb-3 ml-2 chart-actions">
      <el-switch
      :model-value="editMode"
      @update:model-value="value => editMode = value"
      style="--el-switch-on-color: #b47af3"
      active-text="Edit mode"
      inactive-text="Read mode"
    />
    <el-button
        type="text"
        class="revert-button"
        @click="revertAllEdits"
      >
      <el-icon><Refresh /></el-icon>
        Revert all edits
      </el-button>
    </div>
      <apexchart ref="chart" :options="chartOptions" :series="displayedSeries" @markerClick="markerClick"></apexchart>
    </div>
    <div class="toggle-container">
      <label for="toggle-button" theme="bulma"  class="toggle-label">Toggle sensor labels:</label>
      <el-switch
      :model-value="isModified"
            @update:model-value="value => isModified = value"
      style="--el-switch-on-color: #b47af3"

    />
      <label for="toggle-button" class="toggle-label">Toggle percentage changes:</label>
      <el-switch
      :model-value="isPercentageChange"
            @update:model-value="togglePercentageChange"
      style="--el-switch-on-color: #b47af3"

    />
    </div>
  </div>
</template>

<script>
import { onMounted, ref, computed, toRefs, watch } from 'vue'
import VueApexCharts from 'vue3-apexcharts'
import { ElMessageBox, ElMessage } from 'element-plus'
import { useStore } from 'vuex'
import { arrayUnion, arrayRemove } from '@firebase/firestore'
import { updateDocument } from '../../services/useDocument'
import getDocument from '../../services/getDocument'
import { cloneDeep } from 'lodash-es'
import { is } from 'date-fns/locale'

export default {
  name: 'LineChartWithToggle',
  props: ['id', 'series', 'title', 'movements', 'sessionId', 'percentageChangeSeries'],
  components: {
    apexchart: VueApexCharts
  },
  setup (props, context) {
    const isModified = ref(false) // A flag to track the toggle state
    const editMode = ref(false)
    const chart = ref(null)
    const store = useStore()
    const { handlelUpdate } = updateDocument('sessions', props.sessionId)
    const { series } = toRefs(props) // toRefs make props reactive individually
    const { percentageChangeSeries } = toRefs(props)
    const chartSeries = ref(cloneDeep(series.value)) // Create a deep copy of the series prop
    const chartPercentageChangeSeries = ref(cloneDeep(percentageChangeSeries.value))
    const { document: sessionDoc } = getDocument('sessions', props.sessionId)
    const isPercentageChange = ref(false)

    const displayedSeries = computed(() => {
      return isPercentageChange.value ? chartPercentageChangeSeries.value : chartSeries.value
    })

    const chartOptions = computed(() => {
      const isDarkMode = store.state.theme.currentTheme === 'night'
      return {
        chart: {
          background: 'transparent',
          id: props.id,
          type: 'line',
          zoom: {
            type: 'xy',
            enabled: true
          },
          animations: {
            enabled: false
          },
          toolbar: {
            autoSelected: 'zoom',
            export: {
              csv: {
                headerCategory: 'Time',
                dateFormatter (timestamp) {
                  return timestamp + 'ㅤ'
                }
              }
            },
            tools: {
              zoomin: false,
              zoomout: false
            }
          }
        },
        xaxis: {
          type: 'datetime',
          labels: {
            datetimeUTC: false
          },
          title: {
            text: 'Time',
            style: {
              fontSize: '14px',
              cssClass: 'apexcharts-xaxis-title'
            }
          }
        },
        tooltip: {
          x: {
            format: 'HH:mm:ss'
          }
        },
        yaxis: {
          decimalsInFloat: 2,
          title: {
            text: isPercentageChange.value ? 'Percentage change' : 'Sensor value',
            style: {
              fontSize: '14px'
            }
          }
        },
        title: {
          text: props.title,
          style: {
            fontSize: '20px'
          }
        },
        stroke: {
          width: 1.5
        },
        annotations: {
          xaxis: props.movements
        },
        theme: {
          mode: isDarkMode ? 'dark' : 'light',
          palette: 'palette4'
        }
      }
    })

    watch(isModified, (newVal) => {
      chartPercentageChangeSeries.value.forEach((s) => {
        if (newVal) {
          s.originalName = s.name
          s.name = s.alternativeName
        } else {
          s.name = s.originalName
        }
      })
      chartSeries.value.forEach((s) => {
        if (newVal) {
          s.originalName = s.name
          s.name = s.alternativeName
        } else {
          s.name = s.originalName
        }
      })

      chartOptions.value = { ...chartOptions.value }
    })

    onMounted(() => {
      context.emit('chartsLoaded')
    })

    const markerClick = (event, chartContext, { seriesIndex, dataPointIndex, config }) => {
      if (!editMode.value) return
      let point
      if (isPercentageChange.value) {
        point = chartPercentageChangeSeries.value[seriesIndex].data[dataPointIndex]
      } else {
        point = chartSeries.value[seriesIndex].data[dataPointIndex]
      }
      const xValue = new Date(point[0]).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })

      const yValue = point[1].toFixed(2)

      const message = `Are you sure you want to remove the selected point?<br>Time: ${xValue}, Value: ${yValue}`
      ElMessageBox.confirm(message, 'Remove point', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning',
        dangerouslyUseHTMLString: true
      }).then(async () => {
        handlelUpdate({ removedTimePoints: arrayUnion(point[0]) })

        const message = ElMessage({
          message: 'Deleted data point. <a href="#" style="text-decoration: underline;color: #409EFF;" onclick="event.preventDefault(); undoDelete()">Undo</a>',
          type: 'success',
          duration: 3000,
          dangerouslyUseHTMLString: true
        })

        window.undoDelete = () => {
          handlelUpdate({ removedTimePoints: arrayRemove(point[0]) })
          message.close()
        }
      }).catch((e) => {

      }).finally(() => {
      })
    }

    watch(() => sessionDoc, (newSessionDoc) => {
      if (newSessionDoc.value && newSessionDoc.value.removedTimePoints) {
        // Clone the original series to not mutate the prop directly
        const updatedSeries = cloneDeep(series.value)
        const updatedPercentageChangeSeries = cloneDeep(percentageChangeSeries.value)

        const removedTimePointsSet = new Set(newSessionDoc.value.removedTimePoints)

        // Filter out the data points that have been removed
        updatedSeries.forEach(s => {
          s.data = s.data.filter(([x, y]) => !removedTimePointsSet.has(x))
        })
        updatedPercentageChangeSeries.forEach(s => {
          s.data = s.data.filter(([x, y]) => !removedTimePointsSet.has(x))
        })

        chartSeries.value = updatedSeries
        chartPercentageChangeSeries.value = updatedPercentageChangeSeries
      }
    }, { deep: true })

    const revertAllEdits = () => {
      handlelUpdate({ removedTimePoints: [] })
      ElMessage({
        message: 'All edits reverted.',
        type: 'success',
        duration: 3000
      })
    }

    const togglePercentageChange = () => {
      isPercentageChange.value = !isPercentageChange.value
    }

    return {
      chartOptions,
      isModified,
      markerClick,
      editMode,
      chart,
      revertAllEdits,
      isPercentageChange,
      togglePercentageChange,
      displayedSeries
    }
  }
}
</script>

<style>
.line-chart {
  width: 100%;
  margin-bottom: 20px;
}

.toggle-container {
  display: flex;
  align-items: center;
}

.toggle-label {
  margin-right: 15px;
  font-size: 16px;
  margin-left: 15px;
  font-size: 13px;
}

</style>
